import axios from 'axios';
import type { AdditionalDiveData } from '../types/Dive';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// LocalStorage keys for caching in local mode
const STORAGE_KEYS = {
  DIVE_LOCATIONS: 'aidivecoach_dive_locations_cache',
  PREFERENCES: 'aidivecoach_preferences_cache',
  ADDITIONAL_DIVE_DATA: 'aidivecoach_additional_dive_data_cache',
};

export interface UserPreferences {
  userId: string;
  preferredUnit: 'imperial' | 'metric';
  theme?: string;
  showDepthInMeters?: boolean;
  showTemperatureInCelsius?: boolean;
  showPressureInBar?: boolean;
  certification?: string;
  locationDisplayPreference?: 'geotagged' | 'description';
  customSettings?: Record<string, any>;
  updatedAt: string;
}

export interface DiveLocation {
  userId: string;
  diveKey: string;
  latitude: number;
  longitude: number;
  locationName?: string;
  isUserEdited?: boolean;
  notes?: string;
  diveDate: string;
  updatedAt: string;
}

// Helper functions for localStorage cache
const loadFromCache = <T>(key: string): T | null => {
  try {
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.warn('Failed to load from cache:', error);
    return null;
  }
};

const saveToCache = <T>(key: string, data: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save to cache:', error);
  }
};

export const userDataService = {
  // User Preferences
  getPreferences: async (userId: string): Promise<UserPreferences> => {
    try {
      const response = await apiClient.get(`/userdata/preferences/${userId}`);
      
      // If backend returns "(in-memory only)", it means local mode - use cache
      if (response.data && !response.data.persistedToDatabase) {
        const cached = loadFromCache<Record<string, UserPreferences>>(STORAGE_KEYS.PREFERENCES);
        if (cached && cached[userId]) {
          return cached[userId];
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      // Try cache on error
      const cached = loadFromCache<Record<string, UserPreferences>>(STORAGE_KEYS.PREFERENCES);
      if (cached && cached[userId]) {
        return cached[userId];
      }
      throw error;
    }
  },

  savePreferences: async (preferences: UserPreferences): Promise<void> => {
    try {
      await apiClient.post('/userdata/preferences', preferences);
      
      // Always cache to localStorage for local mode persistence
      const cached = loadFromCache<Record<string, UserPreferences>>(STORAGE_KEYS.PREFERENCES) || {};
      cached[preferences.userId] = preferences;
      saveToCache(STORAGE_KEYS.PREFERENCES, cached);
    } catch (error) {
      console.error('Error saving user preferences:', error);
      throw error;
    }
  },

  // Dive Locations
  getDiveLocations: async (userId: string): Promise<DiveLocation[]> => {
    try {
      const response = await apiClient.get(`/userdata/dive-locations/${userId}`);
      
      // If backend returns empty array and we're in local mode, check cache
      if (response.data && response.data.length === 0) {
        const cached = loadFromCache<Record<string, DiveLocation[]>>(STORAGE_KEYS.DIVE_LOCATIONS);
        if (cached && cached[userId] && cached[userId].length > 0) {
          return cached[userId];
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching dive locations:', error);
      // Try cache on error
      const cached = loadFromCache<Record<string, DiveLocation[]>>(STORAGE_KEYS.DIVE_LOCATIONS);
      if (cached && cached[userId]) {
        return cached[userId];
      }
      return [];
    }
  },

  getDiveLocation: async (userId: string, diveKey: string): Promise<DiveLocation | null> => {
    try {
      const response = await apiClient.get(`/userdata/dive-locations/${userId}/${diveKey}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Check cache
        const cached = loadFromCache<Record<string, DiveLocation[]>>(STORAGE_KEYS.DIVE_LOCATIONS);
        if (cached && cached[userId]) {
          const location = cached[userId].find(loc => loc.diveKey === diveKey);
          if (location) {
            return location;
          }
        }
        return null;
      }
      console.error('Error fetching dive location:', error);
      throw error;
    }
  },

  saveDiveLocation: async (location: DiveLocation): Promise<void> => {
    try {
      await apiClient.post('/userdata/dive-locations', location);
      
      // Always cache to localStorage for local mode persistence
      const cached = loadFromCache<Record<string, DiveLocation[]>>(STORAGE_KEYS.DIVE_LOCATIONS) || {};
      if (!cached[location.userId]) {
        cached[location.userId] = [];
      }
      
      // Update or add the location
      const existingIndex = cached[location.userId].findIndex(loc => loc.diveKey === location.diveKey);
      if (existingIndex >= 0) {
        cached[location.userId][existingIndex] = location;
      } else {
        cached[location.userId].push(location);
      }

      saveToCache(STORAGE_KEYS.DIVE_LOCATIONS, cached);
    } catch (error) {
      console.error('Error saving dive location:', error);
      throw error;
    }
  },

  deleteDiveLocation: async (userId: string, diveKey: string): Promise<void> => {
    try {
      await apiClient.delete(`/userdata/dive-locations/${userId}/${diveKey}`);
      
      // Remove from cache
      const cached = loadFromCache<Record<string, DiveLocation[]>>(STORAGE_KEYS.DIVE_LOCATIONS);
      if (cached && cached[userId]) {
        cached[userId] = cached[userId].filter(loc => loc.diveKey !== diveKey);
        saveToCache(STORAGE_KEYS.DIVE_LOCATIONS, cached);
      }
    } catch (error) {
      console.error('Error deleting dive location:', error);
      throw error;
    }
  },

  // Helper: Generate dive key from coordinates and date
  generateDiveKey: (latitude: number, longitude: number, diveDate: Date): string => {
    const lat = latitude.toFixed(4);
    const lng = longitude.toFixed(4);
    // Guard against an invalid Date (e.g. an unparseable dive.startTime), whose
    // toISOString() would throw a RangeError and break the caller. Fall back to a
    // stable "unknown-date" segment so the key stays deterministic per location.
    const date =
      Number.isNaN(diveDate.getTime())
        ? 'unknown-date'
        : diveDate.toISOString().split('T')[0];
    return `${lat}_${lng}_${date}`;
  },

  // Additional Dive Data
  getAdditionalDiveData: async (userId: string, diveKey: string): Promise<AdditionalDiveData> => {
    try {
      const response = await apiClient.get(`/userdata/additional-dive-data/${userId}/${diveKey}`);
      const responseData = response.data || {};

      const hasServerData = Object.keys(responseData).length > 0;
      if (hasServerData) {
        return responseData;
      }

      const cached = loadFromCache<Record<string, Record<string, AdditionalDiveData>>>(
        STORAGE_KEYS.ADDITIONAL_DIVE_DATA
      );
      if (cached && cached[userId] && cached[userId][diveKey]) {
        return cached[userId][diveKey];
      }

      return {};
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 500) {
        // Check cache
        const cached = loadFromCache<Record<string, Record<string, AdditionalDiveData>>>(
          STORAGE_KEYS.ADDITIONAL_DIVE_DATA
        );
        if (cached && cached[userId] && cached[userId][diveKey]) {
          return cached[userId][diveKey];
        }
      }
      console.error('Error fetching additional dive data:', error);
      return {};
    }
  },

  saveAdditionalDiveData: async (userId: string, diveKey: string, data: AdditionalDiveData): Promise<void> => {
    try {
      await apiClient.post('/userdata/additional-dive-data', {
        userId,
        diveKey,
        data,
      });

      // Always cache to localStorage for local mode persistence
      const cached = loadFromCache<Record<string, Record<string, AdditionalDiveData>>>(
        STORAGE_KEYS.ADDITIONAL_DIVE_DATA
      ) || {};
      
      if (!cached[userId]) {
        cached[userId] = {};
      }

      cached[userId][diveKey] = data;
      saveToCache(STORAGE_KEYS.ADDITIONAL_DIVE_DATA, cached);
    } catch (error) {
      console.error('Error saving additional dive data:', error);
      throw error;
    }
  },

  deleteAdditionalDiveData: async (userId: string, diveKey: string): Promise<void> => {
    try {
      await apiClient.delete(`/userdata/additional-dive-data/${userId}/${diveKey}`);

      // Remove from cache
      const cached = loadFromCache<Record<string, Record<string, AdditionalDiveData>>>(
        STORAGE_KEYS.ADDITIONAL_DIVE_DATA
      );
      if (cached && cached[userId] && cached[userId][diveKey]) {
        delete cached[userId][diveKey];
        saveToCache(STORAGE_KEYS.ADDITIONAL_DIVE_DATA, cached);
      }
    } catch (error) {
      console.error('Error deleting additional dive data:', error);
      throw error;
    }
  },
};

// Export types for convenience
export type { AdditionalDiveData, ThermalSuit } from '../types/Dive';
